import { useEffect, useState } from "react";
import type { Collection } from "../api/types";
import { getCollections } from "../api/content";

/**
 * The site's collections, fetched once per page load and shared.
 *
 * <p>Navigation, the section pages, and the "kept in its own section" notice on
 * the galleries all read from this, so adding a collection to
 * {@code collections.json} shows up everywhere without a code change. The list
 * changes about as often as the site is redeployed, so a single module-level
 * cache is plenty — and a failed fetch resolves to an empty list rather than an
 * error, because navigation must still render.</p>
 */
let cache: Collection[] | null = null;
let inFlight: Promise<Collection[]> | null = null;

function load(): Promise<Collection[]> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = getCollections()
      .then((list) => {
        cache = list;
        return list;
      })
      .catch(() => [])
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

export function useCollections(): { collections: Collection[]; loading: boolean } {
  const [collections, setCollections] = useState<Collection[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    let live = true;
    void load().then((list) => {
      if (!live) return;
      setCollections(list);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, []);

  return { collections, loading };
}

/** Only the collections that are browsed in their own section. */
export function separateCollections(collections: Collection[]): Collection[] {
  return collections.filter((c) => c.separate);
}

/** Look up a collection's display label, falling back to its raw id. */
export function collectionLabel(collections: Collection[], id: string): string {
  return collections.find((c) => c.id === id)?.label ?? id;
}

/** Reset the cache. Exported for tests. */
export function resetCollectionsCache(): void {
  cache = null;
  inFlight = null;
}
